import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/api';
import BookingChat from '../components/BookingChat';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ChatCircleText, CircleNotch, CaretRight, Lock, WarningCircle } from '@phosphor-icons/react';

const statusLabel = (s) => ({
  pending: 'In attesa', pending_pro: 'In attesa', notified: 'Inviata', confirmed: 'Confermata',
  accepted: 'Accettata', in_progress: 'In corso', completed: 'Completata', reviewed: 'Completata',
  cancelled: 'Annullata', payment_required: 'Pagamento richiesto',
}[s] || s);

const MessagesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openBooking, setOpenBooking] = useState(null);
  const [openRequest, setOpenRequest] = useState(null);
  const [awaitingCount, setAwaitingCount] = useState(0);

  const fetchConvs = useCallback(async () => {
    try {
      const [b, r] = await Promise.all([
        api.get('/api/conversations').catch(() => ({ data: {} })),
        api.get('/api/request-conversations').catch(() => ({ data: {} })),
      ]);
      const bookings = (b.data?.conversations || []).map((c) => ({ ...c, kind: 'booking', key: c.booking_id }));
      const requests = (r.data?.conversations || []).map((c) => ({ ...c, kind: 'request', key: c.request_id }));
      setAwaitingCount(Number(r.data?.awaiting_reply_count || 0));
      const all = [...requests, ...bookings];
      all.sort((x, y) => (y.last_message_at || '').localeCompare(x.last_message_at || ''));
      setConversations(all);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConvs(); }, [fetchConvs]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const b = params.get('booking');
    const r = params.get('request');
    if (b) setOpenBooking(b);
    if (r) setOpenRequest(r);
  }, [location.search]);

  const closeChat = () => {
    setOpenBooking(null);
    setOpenRequest(null);
    fetchConvs();
    window.dispatchEvent(new Event('vl:notifications-refresh'));
    if (location.search) navigate(location.pathname, { replace: true });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8" data-testid="messages-page">
      <div className="mb-6">
        <h2 className="font-heading font-bold text-2xl text-foreground">Messaggi</h2>
        <p className="text-sm text-slate-500 mt-1">Le tue conversazioni per ogni prestazione. La chat si apre quando la richiesta è assegnata e resta attiva fino al termine della prestazione.</p>
      </div>

      {awaitingCount > 0 && (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="awaiting-reply-banner">
          <WarningCircle size={20} weight="duotone" className="text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900">
              {awaitingCount === 1 ? '1 cliente aspetta' : `${awaitingCount} clienti aspettano`} una risposta da più di un giorno
            </p>
            <p className="text-xs text-amber-800/80">Le conversazioni sono segnalate qui sotto con l'etichetta «In attesa».</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
          <CircleNotch size={18} className="animate-spin" /> Caricamento…
        </div>
      ) : conversations.length === 0 ? (
        <Card className="rounded-2xl p-10 text-center" data-testid="messages-empty">
          <ChatCircleText size={40} weight="duotone" className="text-accent mx-auto mb-3" />
          <p className="text-foreground font-medium">Nessuna conversazione</p>
          <p className="text-sm text-slate-500 mt-1">Le conversazioni compaiono qui quando una richiesta viene assegnata a un professionista.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="conversations-list">
          {conversations.map((c) => (
            <button
              key={c.key}
              onClick={() => (c.kind === 'request' ? setOpenRequest(c.request_id) : setOpenBooking(c.booking_id))}
              data-testid={`conversation-${c.key}`}
              className="w-full text-left bg-white rounded-2xl border border-[#E3F2FD] hover:border-primary/40 hover:shadow-[0_6px_20px_rgba(21,101,192,0.08)] transition-all p-4 flex items-center gap-4"
            >
              <div className="relative shrink-0">
                <div className="w-12 h-12 rounded-full bg-secondary text-primary flex items-center justify-center">
                  <ChatCircleText size={24} weight="duotone" />
                </div>
                {c.unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid={`conv-unread-${c.key}`}>
                    {c.unread > 9 ? '9+' : c.unread}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`truncate ${c.unread > 0 ? 'font-bold text-foreground' : 'font-medium text-foreground'}`}>{c.other_party_name}</span>
                  <Badge variant="outline" className="text-[10px] shrink-0 border-[#E3F2FD] text-slate-500">{statusLabel(c.status)}</Badge>
                  {c.my_role === 'center' && (
                    <Badge className="text-[10px] shrink-0 bg-sky-100 text-sky-800" data-testid={`conv-center-mode-${c.key}`}>{c.center_direct ? 'Chat diretta col cliente' : 'Vigilanza Centro'}</Badge>
                  )}
                  {c.awaiting_reply && (
                    <Badge className="text-[10px] shrink-0 bg-amber-100 text-amber-900" data-testid={`conv-awaiting-${c.key}`}>
                      In attesa da {Math.max(1, Math.floor(c.waiting_hours / 24))}g
                    </Badge>
                  )}
                  {c.readonly && c.my_role !== 'center' && (
                    <span className="flex items-center gap-1 text-[10px] text-slate-400"><Lock size={11} /> sola lettura</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {c.service_type}
                  {c.assigned_professional_name ? ` · ${c.assigned_professional_name}` : ''}
                </div>
                <div className={`text-sm truncate mt-0.5 ${c.unread > 0 ? 'text-foreground' : 'text-slate-400'}`}>
                  {c.last_message || (c.readonly ? c.readonly_reason || '—' : 'Avvia la conversazione…')}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                {c.last_message_at && (
                  <span className="text-[10px] text-slate-400">
                    {new Date(c.last_message_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                  </span>
                )}
                <CaretRight size={16} className="text-slate-300" />
              </div>
            </button>
          ))}
        </div>
      )}

      {openBooking && <BookingChat bookingId={openBooking} onClose={closeChat} />}
      {openRequest && <BookingChat requestId={openRequest} onClose={closeChat} />}
    </div>
  );
};

export default MessagesPage;
